import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { Chip, Tooltip } from '@frameio/vapor';
import { IconBeaker } from '@frameio/vapor-icons';
import { useFeedbackDialogContext } from '../../contexts/ModalContext/ModalContext';
import type { LabsExperimentSlug } from '../../hooks';
import type { FeedbackDialogVariant } from '../FeedbackDialog/FeedbackDialog';
import TEXT from './generated/TEXT/LabsChip';
import styles from './LabsChip.module.css';

TEXT.DEFINE({
  /** Label text for labs chip */
  label: 'Labs',
});

/**
 * Labs that support submitting feedback from the chip, mapped to the shared
 * FeedbackDialog variant opened on click.
 */
const FEEDBACK_VARIANT_BY_LAB: Partial<
  Record<LabsExperimentSlug, FeedbackDialogVariant>
> = {
  firefly_actions: 'fireflyActionsFeedback',
};

type LabsChipTooltip = {
  title: ReactNode;
  details?: ReactNode;
  /** Where the tooltip appears relative to the chip. Defaults to Vapor's `top`. */
  placement?: ComponentProps<typeof Tooltip>['placement'];
};

/**
 * Optional hover tooltip. When provided, hovering the chip shows a Labs-style
 * tooltip (beaker adornment + title + details), matching the AI Assistant
 * chip.
 */
type LabsChipProps =
  | { tooltip?: LabsChipTooltip; clickAction?: 'none'; lab?: never }
  | {
      tooltip?: LabsChipTooltip;
      /** Opens the shared feedback dialog for `lab` when the chip is clicked. */
      clickAction: 'feedback';
      /** The lab this chip represents; selects the feedback dialog variant. */
      lab: LabsExperimentSlug;
    };

export const LabsChip = ({
  tooltip,
  clickAction = 'none',
  lab,
}: LabsChipProps) => {
  const { openFeedbackDialog } = useFeedbackDialogContext();

  const feedbackVariant =
    clickAction === 'feedback' && lab
      ? FEEDBACK_VARIANT_BY_LAB[lab]
      : undefined;

  const interactiveProps = feedbackVariant
    ? {
        as: 'button' as const,
        onClick: (event: MouseEvent) => {
          // The chip is often embedded in a clickable container (e.g. a menu
          // item); stop the click here so only the feedback dialog opens.
          event.stopPropagation();
          openFeedbackDialog(feedbackVariant);
        },
      }
    : undefined;

  const chip = (
    <Chip color="aqua" size="small" variant="ghost" {...interactiveProps}>
      {TEXT.label}
    </Chip>
  );

  if (!tooltip) {
    return chip;
  }

  return (
    <Tooltip
      adornmentBefore={<IconBeaker size={24} />}
      className={styles.tooltip}
      details={tooltip.details}
      placement={tooltip.placement}
      title={tooltip.title}
    >
      {chip}
    </Tooltip>
  );
};
